import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "~/utils/cn";
import Button from "../../ui/Button";
import { formatNumber } from "../../plans";
import { checkoutHref, type LandingData } from "../types";
import { startLetterField } from "./letterField";

// Entrance runs in CSS so the copy shows (and animates) before hydration; reduced motion is flattened by tokens.css.
// The scrim is anchored in px to where the copy sits (bottom on phones, start column on desktop), so contrast holds at any height.
const HERO_CSS = `
@keyframes ha-rise{from{opacity:0;transform:translateY(16px)}}
@keyframes ha-word{from{transform:translateY(115%)}}
.ha-bg{background:radial-gradient(80% 65% at 18% 32%,color-mix(in srgb,var(--c-accent) 26%,var(--c-bg)) 0%,transparent 62%),linear-gradient(180deg,var(--c-bg-2) 0%,var(--c-bg) 100%)}
.ha-scrim{background:linear-gradient(to top,var(--c-bg) 0,color-mix(in srgb,var(--c-bg) 90%,transparent) 430px,color-mix(in srgb,var(--c-bg) 50%,transparent) 540px,transparent 700px)}
@media (min-width:768px){.ha-scrim{background:
  linear-gradient(to top,var(--c-bg) 0%,transparent 22%),
  linear-gradient(to left,color-mix(in srgb,var(--c-bg) 94%,transparent) 0,color-mix(in srgb,var(--c-bg) 88%,transparent) calc(max(var(--gutter),(100% - 1200px) / 2) + 700px),transparent calc(max(var(--gutter),(100% - 1200px) / 2) + 1040px))}}
`;
const enter = (delay: number) => ({ animation: `ha-rise .9s var(--ease-out) ${delay}s both` });

export default function HeroA({ data }: { data: LandingData }) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const hero = useRef<HTMLElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [live, setLive] = useState(false);
  const { scrollYProgress } = useScroll({ target: hero, offset: ["start start", "end start"] });
  // the copy hands off to the next section as the hero scrolls away
  const y = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.15]);

  useEffect(() => {
    const host = hero.current, el = canvas.current;
    if (reduce || !host || !el) return;
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    if (conn?.saveData || /2g/.test(conn?.effectiveType ?? "")) return; // keep the three.js chunk off very slow connections
    let cancelled = false;
    let dispose: (() => void) | undefined;
    const idle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 300));
    const cancelIdle = window.cancelIdleCallback ?? window.clearTimeout;
    const handle = idle(
      () =>
        startLetterField(host, el, { getProgress: () => scrollYProgress.get(), onLive: () => setLive(true), onLost: () => setLive(false) }, () => cancelled)
          .then((d) => (cancelled ? d() : (dispose = d)))
          .catch((err) => console.warn("3D hero disabled:", err)),
      { timeout: 2000 },
    );
    return () => {
      cancelled = true;
      cancelIdle(handle);
      dispose?.();
    };
  }, [reduce, scrollYProgress]);

  const title = t("ui.landing.hero.title");
  const emphasis = t("ui.landing.hero.titleEmphasis");
  const at = title.indexOf(emphasis);
  let pos = 0;
  const words = title.split(" ").map((w) => {
    const start = title.indexOf(w, pos);
    pos = start + w.length;
    return { w, em: at >= 0 && start >= at && start < at + emphasis.length };
  });
  const pro = data.proDaysLeft !== null;

  return (
    <section ref={hero} aria-labelledby="hero-title" className="ha-bg relative isolate grid min-h-[100dvh] overflow-hidden">
      <style dangerouslySetInnerHTML={{ __html: HERO_CSS }} />
      <div aria-hidden className={cn("pointer-events-none absolute inset-0 -z-[3] select-none transition-opacity duration-[1400ms]", live && "opacity-0")}>
        <span className="absolute -end-[4%] top-[6%] font-display text-[min(70vw,620px)] font-black leading-none text-[color-mix(in_srgb,var(--c-accent)_20%,var(--c-bg))] md:end-[2%]">
          ف
        </span>
        <span className="absolute start-[4%] top-[14%] font-alt text-[min(44vw,380px)] font-extralight leading-none text-transparent [-webkit-text-stroke:1px_color-mix(in_srgb,var(--c-accent)_55%,transparent)] md:start-auto md:end-[30%] md:top-[34%]">
          ن
        </span>
        <span className="absolute end-[40%] top-[30%] font-display text-[min(26vw,220px)] font-extrabold leading-none text-[color-mix(in_srgb,var(--c-accent)_16%,var(--c-bg))] md:hidden">
          ت
        </span>
      </div>
      <canvas
        ref={canvas}
        aria-hidden
        className={cn("absolute inset-0 -z-[2] block size-full opacity-0 transition-opacity duration-[1600ms]", live && "opacity-100")}
      />
      <div aria-hidden className="ha-scrim pointer-events-none absolute inset-0 -z-[1]" />

      <motion.div
        style={{ y, opacity }}
        className="motion-reduce:!opacity-100 motion-reduce:![transform:none] mx-auto w-full max-w-[1200px] self-end px-[var(--gutter)] pb-[clamp(36px,6dvh,72px)] pt-[calc(var(--nav-h)+env(safe-area-inset-top)+24px)] md:self-center md:pb-[72px] md:pt-[calc(var(--nav-h)+env(safe-area-inset-top)+32px)]"
      >
        <div className="max-w-[720px]">
          <p className="mb-3.5 font-display text-[15px] font-medium leading-relaxed text-accent-text" style={enter(0.15)}>
            {pro ? t("ui.landing.hero.proUntil", { days: formatNumber(data.proDaysLeft!) }) : t("ui.landing.hero.eyebrow")}
          </p>
          <h1 id="hero-title" className="text-balance font-display text-[clamp(1.75rem,9.6vw_-_3px,4.25rem)] font-black leading-[1.28] text-ink">
            {words.map(({ w, em }, i) => (
              <Fragment key={i}>
                {i > 0 && " "}
                {/* whole words rise out of a mask: Persian letters must stay joined */}
                <span className="-mb-[.2em] -mt-[.1em] inline-block overflow-hidden pb-[.2em] pt-[.1em] align-top">
                  <span className={cn("inline-block", em && "text-accent-text")} style={{ animation: `ha-word 1.1s var(--ease-out) ${0.2 + i * 0.08}s both` }}>
                    {w}
                  </span>
                </span>
              </Fragment>
            ))}
          </h1>
          <p className="mt-[18px] max-w-[32em] text-[clamp(1.0625rem,1rem+.3vw,1.1875rem)] leading-[1.9] text-ink-2" style={enter(0.6)}>
            {t("ui.landing.hero.subtitle")}
          </p>
          <div id="hero-cta" className="mt-[30px]" style={enter(0.75)}>
            <Button to={checkoutHref()} prefetch="intent" size="lg" className="h-[60px] px-8 font-display text-lg">
              {pro ? t("ui.landing.hero.ctaRenew") : t("ui.landing.hero.cta")}
              <ArrowLeft className="size-5 ltr:rotate-180" strokeWidth={1.75} aria-hidden />
            </Button>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
